/**
 * 15 #9 — Consultant turnaround per project and recipient: how many days replies took (where the reply date is
 * recorded), how many still wait, how many are overdue (90+ days), the median and the longest wait. Slowest first;
 * a row filters the Submission Log to that project.
 */
import { useMemo } from 'react';
import { Gauge } from 'lucide-react';
import { SidePanel, SidePanelSection } from '@/components/ui/side-panel';
import { formatDays, formatNumber } from '@/lib/format';
import { turnaroundStats } from './model';
import { AgeText } from './SdUi';

const fmt = (value) => formatNumber(value, { maxDecimals: 0 });

export function TurnaroundPanel({ open, onOpenChange, rows, onPickProject }) {
  const stats = useMemo(() => turnaroundStats(rows), [rows]);
  const overdue = stats.rows.reduce((sum, item) => sum + item.overdue, 0);
  const awaiting = stats.rows.reduce((sum, item) => sum + item.awaiting, 0);
  const replied = stats.rows.reduce((sum, item) => sum + item.replied, 0);
  const slowest = stats.slowest;
  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="xl"
      title="Consultant turnaround"
      subtitle="Per project and recipient · reply times where the reply date is recorded"
      icon={Gauge}
      iconTone="navy"
      hideFooter
      testId="turnaround-panel"
    >
      <SidePanelSection title="In short">
        <div className="grid gap-2 sm:grid-cols-3">
          <Fact label="Awaiting a reply" value={fmt(awaiting)} sub={`${fmt(overdue)} for more than 90 days`} tone={overdue ? 'red' : undefined} />
          <Fact label="Replies with a date" value={fmt(replied)} sub={replied ? 'used for the average reply time' : 'record replies to measure turnaround'} />
          <Fact
            label="Slowest"
            value={slowest ? `${slowest.recipient} · ${slowest.project}` : '—'}
            sub={slowest ? (slowest.avgReplyDays !== null ? `replies after ${formatDays(slowest.avgReplyDays)} on average` : `${fmt(slowest.awaiting)} waiting, median ${formatDays(slowest.medianWaiting)}`) : ''}
            small
          />
        </div>
      </SidePanelSection>
      <SidePanelSection title={`${fmt(stats.rows.length)} project · recipient pairs`} description="Slowest first. Choose one to filter the log.">
        <div className="overflow-x-auto rounded-xl border border-line">
          <table className="w-full min-w-[640px] border-separate border-spacing-0 text-[12.5px]" data-testid="turnaround-table">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-[0.04em] text-ink-3">
                {['Project · sent to', 'Sent', 'Avg reply', 'Awaiting', '90+ d', 'Median wait', 'Longest wait'].map((head, index) => (
                  <th key={head} scope="col" className={`h-8 border-b border-line bg-surface-2 px-2.5 ${index ? 'text-right' : ''}`}>{head}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stats.rows.map((item) => (
                <tr key={item.key} className="cursor-pointer hover:bg-surface-hover" onClick={() => onPickProject(item.project)} data-testid={`turnaround-row-${item.key}`}>
                  <td className="border-b border-line-2 px-2.5 py-2">
                    <button type="button" className="text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={(event) => { event.stopPropagation(); onPickProject(item.project); }}>
                      <span className="block font-semibold text-ink">{item.project}{item.projectNo ? <span className="ml-1 font-mono text-[11px] font-medium text-ink-3">#{item.projectNo}</span> : null}</span>
                      <span className="block text-[11.5px] text-ink-3">to {item.recipient}</span>
                    </button>
                  </td>
                  <td className="border-b border-line-2 px-2.5 py-2 text-right tabular-nums text-ink-2">{fmt(item.submissions)}</td>
                  <td className="border-b border-line-2 px-2.5 py-2 text-right">{item.avgReplyDays !== null ? <span title={`${fmt(item.replied)} replies with a date`}><AgeText days={item.avgReplyDays} className="font-bold" /></span> : <span className="text-ink-4">—</span>}</td>
                  <td className="border-b border-line-2 px-2.5 py-2 text-right font-semibold tabular-nums text-ink">{fmt(item.awaiting)}</td>
                  <td className="border-b border-line-2 px-2.5 py-2 text-right tabular-nums">{item.overdue ? <b className="text-status-red-ink">{fmt(item.overdue)}</b> : <span className="text-ink-4">0</span>}</td>
                  <td className="border-b border-line-2 px-2.5 py-2 text-right"><AgeText days={item.medianWaiting} /></td>
                  <td className="border-b border-line-2 px-2.5 py-2 text-right"><AgeText days={item.oldestWaiting} className="font-bold" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SidePanelSection>
    </SidePanel>
  );
}

function Fact({ label, value, sub, tone, small = false }) {
  return (
    <div className="rounded-xl border border-line bg-surface px-3 py-2.5">
      <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">{label}</p>
      <p className={`mt-0.5 truncate font-heading font-black ${small ? 'text-[15px]' : 'text-[22px]'} ${tone === 'red' ? 'text-status-red-ink' : 'text-ink'}`} title={typeof value === 'string' ? value : undefined}>{value}</p>
      {sub ? <p className="truncate text-[12px] text-ink-3">{sub}</p> : null}
    </div>
  );
}
